import type { HazardType } from "@shared/scoring/types";

/** The pipeline's exact note on a precipitation season left unscored by the
 * dry-site rule (team decision 28 Oct 2026; precipitation_*_eam_v2.csv). The
 * app shows the row's own note text; this copy is for the report basis. */
export const PRECIPITATION_DRY_SITE_NOTE =
  "Seasonal rainfall is <50mm, inflating the percent change. Hence, not scored as physical hazard does not pose a material risk";

/**
 * Methodology-dependent wording for the Tab 4 report. This is the ONLY place
 * such text lives: when an open/provisional spec item resolves (see the
 * design spec's "Working assumptions" table), the fix is a text change here —
 * never a component change.
 */
export interface HazardBasis {
  /** The hazard's indicator (functional spec §4). */
  indicator: string;
  /** Data baseline window of the current leg — a fact about the data, NOT an
   * assessment year. null when no window is stated (Water Stress records
   * none; Changing Precipitation is not assessed). */
  baselinePeriod: string | null;
  /** Extra sentences printed against this hazard (e.g. a Drought "under
   * review" line). Empty for every hazard today. */
  notes: string[];
  /** Printed only when the hazard has >= 1 computed future-horizon row in
   * the report AND this is set (e.g. the CHC-CMIP6 blended-ensemble wording
   * once Heat Stress / Precipitation future data exists). */
  futureSourceNote?: string;
}

/**
 * Baseline periods re-verified 2026-10-31 against the loaded time_period
 * labels (the loaders pass the pipeline CSV's time_period through): Heat
 * Stress "historical (1991-2020)", Drought "Current (1991-2020)", Floods and
 * Landslide "historical (1991-2020)" (moved from 1990-2020 on 31 Oct 2026).
 * Re-verify whenever a leg is reloaded.
 */
export const HAZARD_BASIS: Record<HazardType, HazardBasis> = {
  heat_stress: { indicator: "TXx (annual maximum of daily maximum temperature)", baselinePeriod: "1991–2020", notes: [] },
  changing_precipitation: {
    indicator:
      "Seasonal rainfall anomaly, March–May (MAM) and October–December (OND), scored separately and never combined; " +
      "Malawi sites: the site's main rainy season only",
    baselinePeriod: "1991–2020",
    notes: [
      "Current: the most recently completed season against its 1991–2020 average (CHIRPS).",
      "Malawi sites are scored on their main rainy season only — the site's single wettest three-month window " +
        "(December–February, January–March or February–April) — and appear as \"main rainy season\" lines, not MAM/OND.",
      `Seasons with a baseline under 50 mm are not scored: "${PRECIPITATION_DRY_SITE_NOTE}".`,
    ],
    futureSourceNote:
      "Projected precipitation is the change in the CMIP6 ensemble median (World Bank CCKP) against the models' own 1995–2014 period.",
  },
  water_stress: { indicator: "WRI Aqueduct Baseline Water Stress", baselinePeriod: null, notes: [] },
  drought: {
    indicator:
      "Aridity index (rainfall ÷ potential evaporation, 1991–2020) for the current score; projected change in how often rainy seasons are in drought (SPEI-3 ≤ −1.0) for future scores. Future score = current score adjusted for the projected change, capped at 4.",
    baselinePeriod: "1991–2020",
    notes: [],
  },
  floods: { indicator: "Rx5day (annual maximum 5-day rainfall)", baselinePeriod: "1991–2020", notes: [] },
  landslide: { indicator: "LHASA landslide susceptibility combined with Rx3day (annual maximum 3-day rainfall)", baselinePeriod: "1991–2020", notes: [] },
};

export const ALIGNMENT_STATEMENT =
  "Alignment with VSME, ESRS and IFRS S1–S2 has not been assessed; this report makes no claim of alignment.";

export const BASIS_TEXT = {
  scope: "Risk shown is gross risk, before any adaptation or mitigation measures.",
  scale:
    "Risk is scored on a 1–4 scale: 1 = Low, 2 = Medium, 3 = High, 4 = Very High. " +
    "An asset is counted \"at high risk\" for a hazard when its score is 3 or 4.",
  method:
    "Each asset–hazard risk is looked up in an Exposure × Vulnerability matrix. " +
    "Vulnerability combines the asset's business importance with the physical impact typical for its asset type. " +
    "Exposure is the hazard indicator's own 1–4 score.",
  coordinates: "Hazard values are read from gridded hazard data at each asset's coordinates.",
  /** Cover's one-line restatement of `scope`. Kept here so the two can't drift apart in JSX. */
  coverBasis: "Gross risk, before adaptation measures",
  notAssessed: "Not assessed: no computed results.",
  topRisksNote:
    "Ranked by highest computed risk across the selected horizons, then asset importance. Combinations with no computed score on any selected horizon are excluded.",
  shareNote:
    "Share of assessed assets scoring High or Very High. Combinations without a computed score are excluded from every percentage, never counted as zero.",
  businessCritical: "Business-critical assets are those with a business importance of High or Very High.",
} as const;
